import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { mixed } from "yup";
import { Pageable } from "../../../services/pageable";
import { getFields, validationFields } from "../../common/models/fields";
import { AllowedBlockchains, ProductionBlockchains } from "./internal";

export namespace ChainDTO {
  export namespace Requests {
    export namespace PostWalletCheck {
      export interface T {
        address: string;
        blockchain: AllowedBlockchains;
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");

        const addressSchema = validationFields.address.useSchema();

        const address = {
          name: validationFields.address.useName(),
          schema: addressSchema,
        };

        const blockchain = {
          name: validationFields.blockchain.useName(),
          schema: useMemo(
            () =>
              mixed<AllowedBlockchains>()
                .oneOf(Object.keys(ProductionBlockchains.enum) as Array<AllowedBlockchains>)
                .label(t("blockchain")),
            [t]
          ),
        };

        return useMemo(
          () =>
            getFields({
              address,
              blockchain,
            }),
          [address.name, address.schema, blockchain.name, blockchain.schema]
        );
      };
    }
  }
  export namespace Responses {
    export namespace GetSingleWalletReports {
      export interface T {
        createdAt: Date;
        id: string;
        riskScore: number | null;
        chain: AllowedBlockchains;
        address: string;
        riskProfile: {
          atm: number;
          darknetMarketplaces: number;
          darknetServices: number;
          gamblingServices: number;
          illegalServices: number;
          onlineMarketplaces: number;
          miner: number;
          mixer: number;
          paymentProcessors: number;
          ransom: number;
          scam: number;
          stolenCoins: number;
          onlineWallet: number;
          exchangeRiskProfile: {
            highRiskExchangesScore: number;
            moderateRiskExchangesScore: number;
            lowRiskExchangesScore: number;
            veryHighRiskExchangesScore: number;
            fraudulentExchangesScore: number;
          };
          p2pExchangeRiskProfile: {
            highRiskP2PExchangesScore: number;
            lowRiskP2PExchangesScore: number;
          };
        };
      }
    }
    export namespace GetAllWalletReports {
      export interface T {
        content: GetSingleWalletReports.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }
  }
}
